<template>
  <v-card class="payment-method-list mt-10">
    <v-card-text>
      <div class="d-flex align-center mb-4">
        <h2 v-if="showTitle" class="text-h6 mr-4">
          {{ $t('payment-method.payment-methods') || 'Payment methods' }}
          <span v-if="pagination.total > 0" class="text-caption text--secondary ml-2">({{ pagination.total }})</span>
        </h2>
        <v-spacer></v-spacer>
        <v-menu offset-y v-if="!hideActions">
          <template v-slot:activator="{ on, attrs }">
            <v-btn small outlined v-bind="attrs" v-on="on" class="mr-2">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('payment-method-ui.add') }}
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="openAttach('card')">
              <v-list-item-icon><v-icon small>mdi-credit-card-outline</v-icon></v-list-item-icon>
              <v-list-item-content>{{ $t('payment-method-ui.add-card') }}</v-list-item-content>
            </v-list-item>
            <v-list-item @click="openAttach('sepa_debit')">
              <v-list-item-icon><v-icon small>mdi-bank</v-icon></v-list-item-icon>
              <v-list-item-content>{{ $t('payment-method-ui.add-sepa') }}</v-list-item-content>
            </v-list-item>
            <v-list-item @click="openAttach('bancontact')">
              <v-list-item-icon><v-icon small>mdi-credit-card-settings-outline</v-icon></v-list-item-icon>
              <v-list-item-content>{{ $t('payment-method-ui.add-bancontact') }}</v-list-item-content>
            </v-list-item>
            <v-list-item @click="openAttach('apple_pay')">
              <v-list-item-icon><v-icon small>mdi-apple</v-icon></v-list-item-icon>
              <v-list-item-content>{{ $t('payment-method-ui.add-apple-pay') }}</v-list-item-content>
            </v-list-item>
            <v-list-item @click="openAttach('google_pay')">
              <v-list-item-icon><v-icon small>mdi-google</v-icon></v-list-item-icon>
              <v-list-item-content>{{ $t('payment-method-ui.add-google-pay') }}</v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn small color="primary" @click="manualRefresh" :loading="syncLoading" v-if="!hideActions">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('payout.sync-from-stripe') }}
        </v-btn>
        <slot name="actions"></slot>
      </div>

      <v-alert v-if="error" type="error" outlined dense class="mb-4">
        {{ error }}
      </v-alert>

      <!-- Status Filter -->
      <div class="mb-4">
        <v-checkbox
          v-model="showInactive"
          :label="$t('payment-method-ui.show-inactive')"
          dense
          hide-details
          @change="applyStatusFilters"
        ></v-checkbox>
      </div>


      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :server-items-length="pagination.total"
        :options.sync="tableOptions"
        :footer-props="{
          'items-per-page-options': [10, 20, 50],
        }"
        @update:options="onTableOptionsChange"
      >
        <!-- Type column -->
        <template v-slot:item.type="{ item }">
          <v-chip x-small>{{ typeLabel(item.type) }}</v-chip>
        </template>

        <!-- Status column -->
        <template v-slot:item.status="{ item }">
          <v-chip v-if="item.validated === true" x-small color="green" outlined>
            <v-icon x-small left>mdi-check-circle</v-icon>
            {{ $t('validated') }}
          </v-chip>
          <v-chip v-else-if="requiresAuthentication(item)" x-small color="orange" outlined>
            <v-icon x-small left>mdi-lock-alert</v-icon>
            {{ $t('payment-method-ui.requires-authentication') }}
          </v-chip>
          <v-chip v-else x-small :color="statusColor(item.status)" dark>{{ item.status }}</v-chip>
        </template>

        <!-- Created column -->
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt || item.created) }}
        </template>

        <!-- Billing name column -->
        <template v-slot:item.billingName="{ item }">
          {{ item.details?.billing?.name || (item.card && item.card.name) || '' }}
        </template>

        <!-- Summary column -->
        <template v-slot:item.summary="{ item }">
          <div>
            <div class="d-flex align-center">
              <span>{{ item.summary || item.pmSummary || item.details?.summary }}</span>
              <!-- Badge si généré depuis Bancontact -->
              <v-chip v-if="item.generatedFrom === 'bancontact'" x-small color="blue" outlined class="ml-2">
                <v-icon x-small left>mdi-bank-transfer</v-icon>
                Bancontact
              </v-chip>
            </div>
            <div v-if="item.mandate && (item.mandate.url || item.mandate.reference || item.mandate.id)" class="text--secondary text-caption">
              {{ $t('payment-method-ui.mandate') }}:
              <template v-if="item.mandate.url">
                <a :href="item.mandate.url" target="_blank">{{ item.mandate.reference || item.mandate.id || $t('view') }}</a>
              </template>
              <template v-else>
                {{ item.mandate.reference || item.mandate.id }}
              </template>
            </div>
          </div>
        </template>

        <!-- Actions column -->
        <template v-slot:item.actions="{ item }" v-if="!hideActions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small icon v-bind="attrs" v-on="on" @click="openDetail(item)">
                <v-icon color="primary">mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('open') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small icon v-bind="attrs" v-on="on" @click="openTransactions(item)">
                <v-icon color="blue">mdi-history</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('payment-method-ui.transactions') }}</span>
          </v-tooltip>

          <v-tooltip bottom v-if="currentUser.permissions.includes('PAYMENT_ALLOW_CHARGE_FOR_TEST')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small icon v-bind="attrs" v-on="on" :title="$t('payment-method-ui.charge')" @click="openCharge(item)">
                <v-icon color="primary">mdi-cash-multiple</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('payment-method-ui.charge') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn x-small icon v-bind="attrs" v-on="on" :title="$t('payment-method-ui.edit-billing-name')" @click="openEditBilling(item)">
                <v-icon color="grey darken-1">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('payment-method-ui.edit-billing-name') }}</span>
          </v-tooltip>

          <template v-if="requiresAuthentication(item)">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn x-small icon v-bind="attrs" v-on="on" :title="$t('payment-method-ui.authenticate')" @click="retryAuthentication(item)">
                  <v-icon color="orange">mdi-shield-lock</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('payment-method-ui.authenticate') }}</span>
            </v-tooltip>
          </template>

          <v-btn x-small icon :title="$t('delete')" @click="confirmDetach(item)">
            <v-icon color="error">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Attach Payment Method Dialog -->
      <v-dialog v-model="attachDialog" max-width="600px">
        <v-card>
          <v-card-title class="headline">
            {{ $t('payment-method-ui.add') }}
          </v-card-title>
          <v-card-text>
            <div class="mb-2 text--secondary">{{ attachTypeLabel }}</div>

            <!-- Stripe Card collect -->
            <template v-if="attachType === 'card'">
              <v-text-field v-model="cardName"
                            :label="$t('payment-method-ui.cardholder-name')"
                            outlined dense clearable class="mb-2"></v-text-field>
              <div class="mb-1 text--secondary">{{ $t('payment-method-ui.card-details') }}</div>
              <div id="zms-card-element" style="border:1px solid #ccc;border-radius:4px;padding:10px;"></div>
              <div v-if="cardError" class="error--text text-caption mt-1">{{ cardError }}</div>
              <div class="d-flex mt-3">
                <v-spacer></v-spacer>
              </div>
            </template>

            <!-- SEPA Direct Debit collect -->
            <template v-if="attachType === 'sepa_debit'">
              <v-text-field v-model="ibanName"
                            :label="$t('payment-method-ui.debtor-name')"
                            outlined dense clearable class="mb-2"></v-text-field>
              <v-text-field v-model="ibanEmail"
                            :label="$t('payment-method-ui.debtor-email')"
                            outlined dense clearable class="mb-2"></v-text-field>

              <!-- Signatory info (for organizations - required) -->
              <template v-if="isOrganizationContext || isCustomerContext">
                <v-divider class="my-4"></v-divider>
                <div class="text-subtitle-2 mb-2">
                  {{ $t('payment-method-ui.signatory-info') }}
                  <span v-if="isOrganizationContext" class="error--text">*</span>
                </div>
                <v-alert type="info" dense outlined class="mb-3">
                  <div class="text-caption">
                    {{ $t('payment-method-ui.signatory-hint') }}
                  </div>
                </v-alert>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="signatoryFirstName"
                                  :label="$t('firstname') + (isOrganizationContext ? ' *' : '')"
                                  :rules="isOrganizationContext ? [v => !!v || $t('rules.field-required')] : []"
                                  outlined dense clearable
                                  placeholder="Martin"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="signatoryLastName"
                                  :label="$t('lastname') + (isOrganizationContext ? ' *' : '')"
                                  :rules="isOrganizationContext ? [v => !!v || $t('rules.field-required')] : []"
                                  outlined dense clearable
                                  placeholder="Dupond"></v-text-field>
                  </v-col>
                </v-row>
              </template>

              <v-divider class="my-4"></v-divider>

              <div class="mb-1 text--secondary">{{ $t('payment-method-ui.iban') }}</div>
              <div id="zms-iban-element" style="border:1px solid #ccc;border-radius:4px;padding:10px;"></div>
              <div v-if="ibanError" class="error--text text-caption mt-1">{{ ibanError }}</div>

              <v-divider class="my-4"></v-divider>

              <div class="text-body-2 text--secondary">{{ $t('payment-method-ui.sepa-mandate-info') }}</div>
              <v-checkbox v-model="sepaAccepted"
                          :label="$t('payment-method-ui.sepa-initial-acceptance')"
                          dense hide-details class="mt-2"></v-checkbox>
            </template>

            <!-- Bancontact -->
            <template v-if="attachType === 'bancontact'">
              <v-alert type="warning" outlined dense v-if="currentCurrency !== 'EUR'">
                {{ $t('payment-method-ui.only-eur-bancontact') }}
              </v-alert>
              <v-text-field v-model="contactName" :label="$t('payment-method-ui.contact-name')" outlined dense required></v-text-field>
              <v-text-field v-model="contactEmail" :label="$t('payment-method-ui.contact-email')" outlined dense required></v-text-field>
              <v-alert v-if="errorBanner" type="error" dense outlined aria-live="polite">{{ errorBanner }}</v-alert>
              <div class="d-flex mt-3">
                <v-spacer></v-spacer>
                <v-btn small color="primary"
                       :disabled="!contactName || !contactEmail || currentCurrency !== 'EUR' || submitting"
                       :loading="submitting || redirecting"
                       @click="submitBancontact">
                  <v-icon left small>mdi-credit-card-check</v-icon>{{ $t('payment-method-ui.pay-with-bancontact') }}
                </v-btn>
              </div>
              <div v-if="redirecting" class="mt-2 text--secondary" aria-live="polite">{{ $t('payment-method-ui.redirecting') }}
                <a v-if="redirectUrl" :href="redirectUrl" target="_blank">{{ $t('payment-method-ui.click-if-nothing') }}</a>
              </div>
              <v-divider class="my-4"></v-divider>
            </template>

            <!-- Apple Pay -->
            <template v-if="attachType === 'apple_pay'">
              <div class="mb-2 text--secondary">{{ $t('payment-method-ui.apple-pay-desc') }}</div>
              <v-text-field v-model="contactName" :label="$t('payment-method-ui.contact-name')" outlined dense required></v-text-field>
              <v-text-field v-model="contactEmail" :label="$t('payment-method-ui.contact-email')" outlined dense required></v-text-field>
              <v-checkbox v-model="saveForLater" :label="$t('payment-method-ui.save-for-later')" dense hide-details></v-checkbox>

              <!-- Apple Pay Button Container -->
              <div v-if="appleAvailable" id="zms-apple-pay-button" class="my-3"></div>
              <div v-if="!appleAvailable" class="text--disabled mt-2">{{ $t('payment-method-ui.not-available-apple') }}</div>

              <v-alert v-if="errorBanner" type="error" dense outlined aria-live="polite">{{ errorBanner }}</v-alert>
            </template>

            <!-- Google Pay -->
            <template v-if="attachType === 'google_pay'">
              <div class="mb-2 text--secondary">{{ $t('payment-method-ui.google-pay-desc') }}</div>
              <v-text-field v-model="contactName" :label="$t('payment-method-ui.contact-name')" outlined dense required></v-text-field>
              <v-text-field v-model="contactEmail" :label="$t('payment-method-ui.contact-email')" outlined dense required></v-text-field>
              <v-checkbox v-model="saveForLater" :label="$t('payment-method-ui.save-for-later')" dense hide-details></v-checkbox>

              <!-- Google Pay Button Container -->
              <div v-if="googleAvailable" id="zms-google-pay-button" class="my-3"></div>
              <div v-if="!googleAvailable" class="text--disabled mt-2">{{ $t('payment-method-ui.not-available-google') }}</div>

              <v-alert v-if="errorBanner" type="error" dense outlined aria-live="polite">{{ errorBanner }}</v-alert>
            </template>

            <v-alert type="info" v-if="attachResult?.status && attachResult.status !== 'succeeded'" dense outlined>
              <div><b>Status:</b> {{ attachResult.status }}</div>
              <div v-if="attachResult.nextAction">
                <v-btn small color="orange" @click="openAuth(attachResult.nextAction)">
                  <v-icon left small>mdi-open-in-new</v-icon>{{ $t('payment-method-ui.continue-auth') }}
                </v-btn>
              </div>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small text @click="attachDialog=false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" :loading="actionLoading || pmCreating || submitting || redirecting" :disabled="isBottomDisabled()" @click="submitAttach">{{ $t('validate') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Billing Name Dialog -->
      <v-dialog v-model="editDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">
            {{ $t('payment-method-ui.edit-billing-name') }}
          </v-card-title>
          <v-card-text>
            <v-text-field v-model="editForm.name"
                          :label="$t('payment-method-ui.billing-name')"
                          outlined dense clearable></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small text @click="editDialog=false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" :loading="actionLoading" @click="saveBillingName">{{ $t('save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Charge Dialog -->
      <v-dialog v-model="chargeDialog" max-width="600px">
        <v-card>
          <v-card-title class="headline">
            {{ $t('payment-method-ui.charge') }}
          </v-card-title>
          <v-card-text>
            <div class="mb-2 text--secondary">{{ $t('payment-method-ui.charge-description') }}</div>
            <v-text-field v-model.number="chargeForm.amount" type="number" min="0"
                          :label="$t('amount')" outlined dense></v-text-field>
            <v-text-field v-model="chargeForm.currency" :label="$t('currency')" outlined dense></v-text-field>
            <v-switch v-model="chargeForm.capture" :label="$t('payment-method-ui.capture-immediately')" inset></v-switch>
            <v-textarea v-model="chargeForm.metadataRaw" :label="$t('payment-method-ui.metadata')" outlined dense rows="3"></v-textarea>
            <v-alert type="success" v-if="chargeResult" dense outlined class="mt-2">
              <div><b>{{ $t('payment-method-ui.result') }}:</b> <pre style="white-space: pre-wrap;">{{ chargeResult }}</pre></div>
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small text @click="chargeDialog=false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" :loading="actionLoading" @click="submitCharge">{{ $t('validate') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- SEPA Mandate Dialog -->
      <v-dialog v-model="sepaMandateDialog" max-width="800px" persistent>
        <v-card>
          <v-card-title class="headline">
            {{ $t('payment-method-ui.sepa-mandate-title') }}
          </v-card-title>
          <v-card-text>
            <v-alert v-if="mandateError" type="error" dense outlined class="mb-4">
              {{ mandateError }}
            </v-alert>

            <v-progress-linear v-if="mandateLoading" indeterminate color="primary" class="mb-4"/>

            <template v-if="mandateHtml && !mandateLoading">
              <!-- Mandate HTML content -->
              <div class="sepa-mandate-box pa-4 mb-4" style="max-height: 400px; overflow-y: auto; border: 1px solid #ccc; border-radius: 4px;">
                <div v-html="mandateHtml"></div>
              </div>

              <!-- Technical info -->
              <v-expansion-panels flat class="mb-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div class="text-caption text--secondary">
                      <v-icon small left>mdi-information-outline</v-icon>
                      {{ $t('payment-method-ui.technical-info') }}
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="text-caption">
                      <div><strong>Content Hash:</strong> {{ mandateContentHash }}</div>
                      <div><strong>Payment Method ID:</strong> {{ mandatePaymentMethodId }}</div>
                      <div v-if="mandateSnapshotData">
                        <div><strong>Debtor:</strong> {{ mandateSnapshotData.debtorLegalName }}</div>
                        <div><strong>Signatory:</strong> {{ mandateSnapshotData.signatoryName }}</div>
                        <div><strong>IBAN Last 4:</strong> {{ mandateSnapshotData.ibanLast4 }}</div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <!-- Consent checkbox -->
              <v-checkbox v-model="mandateConsent"
                          :label="$t('payment-method-ui.sepa-acceptance')"
                          dense hide-details class="mb-2"></v-checkbox>

              <!-- Success state with PDF link -->
              <v-alert v-if="mandateAccepted && mandatePdfUrl" type="success" dense outlined class="mt-4">
                <div>{{ $t('payment-method-ui.mandate-accepted') }}</div>
                <v-btn small text color="primary" :href="mandatePdfUrl" target="_blank" class="mt-2">
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  {{ $t('payment-method-ui.download-mandate-pdf') }}
                </v-btn>
              </v-alert>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small text @click="closeSepaMandateDialog" :disabled="mandateLoading || mandateAccepting">
              {{ mandateAccepted ? $t('close') : $t('cancel') }}
            </v-btn>
            <v-btn v-if="!mandateAccepted" small color="primary" :loading="mandateAccepting" :disabled="!mandateConsent || mandateLoading" @click="acceptSepaMandateSignature">
              {{ $t('validate') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Payment Method Detail Dialog -->
      <payment-method-detail-dialog
          v-if="detailDialog"
        v-model="detailDialog"
        :payment-method="selectedPaymentMethod"
        :organization-id="organizationId"
        :customer-id="customerId"
      />

      <!-- Transactions Dialog -->
      <payment-transaction-list-modal
        v-if="transactionsDialog"
        v-model="transactionsDialog"
        :payment-method-id="selectedPaymentMethod?.id"
      />

      <!-- Payment Authentication Dialog -->
      <payment-auth-dialog
        v-model="authDialog"
        :payment-response="authPaymentResponse"
        @success="handleAuthSuccess"
        @error="handleAuthError"
        @close="authDialog = false"
      />
    </v-card-text>
    </v-card>
</template>

<script>
import PaymentMethodRepository from '@repository/PaymentMethodRepository'
import StripeService from '@service/StripeService'
import PaymentMethodDetailDialog from '@pages/Organization/Detail/PaymentMethod/PaymentMethodDetailDialog.vue'
import PaymentAuthDialog from '@elements/PaymentAuthDialog.vue'
import PaymentTransactionListModal from '@elements/PaymentTransactionListModal.vue'

export default {
  name: 'PaymentMethodList',

  components: {
    PaymentMethodDetailDialog,
    PaymentAuthDialog,
    PaymentTransactionListModal
  },

  props: {
    organizationId: {
      type: String,
      default: null
    },
    customerId: {
      type: String,
      default: null
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    autoLoad: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      items: [],
      loading: false,
      syncLoading: false,
      error: null,
      ownerInfo: null, // Informations du propriétaire (enrichies par le backend)
      // Pagination
      pagination: {
        total: 0,
        limit: 10,
        offset: 0,
        hasMore: false
      },
      filters: {
        type: null,
        status: null
      },
      // Status filter - simple checkbox
      showInactive: false,
      // v-data-table
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false
      },
      attachDialog: false,
      editDialog: false,
      chargeDialog: false,
      detailDialog: false,
      transactionsDialog: false,
      selectedPaymentMethod: null,
      actionLoading: false,
      // Payment Authentication
      authDialog: false,
      authPaymentResponse: null,
      attachType: null,
      attachForm: { paymentMethodId: '' },
      attachResult: null,
      contactName: '',
      contactEmail: '',
      submitting: false,
      redirecting: false,
      errorBanner: null,
      saveForLater: false,
      paymentRequest: null,
      prButtonElement: null,
      appleAvailable: false,
      googleAvailable: false,
      editTarget: null,
      editForm: { name: '' },
      chargeTarget: null,
      chargeForm: { amount: null, currency: 'EUR', capture: true, metadataRaw: '' },
      chargeResult: null,
      stripe: null,
      elements: null,
      cardElement: null,
      cardError: null,
      cardName: '',
      pmCreating: false,
      ibanElement: null,
      ibanError: null,
      ibanName: '',
      ibanEmail: '',
      signatoryFirstName: '',
      signatoryLastName: '',
      sepaAccepted: false,
      sepaClientSecret: null, // SetupIntent clientSecret for SEPA confirmation
      // SEPA mandate dialog
      sepaMandateDialog: false,
      mandateLoading: false,
      mandateError: null,
      mandateHtml: null,
      mandateContentHash: null,
      mandateSnapshotData: null,
      mandatePaymentMethodId: null,
      mandateMandateId: null,
      mandateConsent: false,
      mandateAccepting: false,
      mandateAccepted: false,
      mandatePdfUrl: null,
    }
  },

  computed: {
    apiParams() {
      // Return the appropriate params based on which prop is provided
      if (this.organizationId) {
        return { organizationId: this.organizationId }
      } else if (this.customerId) {
        return { customerId: this.customerId }
      }
      return {}
    },
    isOrganizationContext() {
      return !!this.organizationId
    },
    isCustomerContext() {
      return !!this.customerId
    },
    currentCurrency() {
      // Try to get currency from ownerInfo (enriched by backend)
      if (this.ownerInfo?.details?.billingCurrency) {
        return this.ownerInfo.details.billingCurrency.toUpperCase()
      }
      if (this.ownerInfo?.details?.currency) {
        return this.ownerInfo.details.currency.toUpperCase()
      }
      return 'EUR'
    },

    // Compute the statuses array based on checkbox
    selectedStatuses() {
      if (this.showInactive) {
        // Show all statuses
        return ['active', 'succeeded', 'inactive', 'detached', 'expired']
      } else {
        // Show only active statuses (default)
        return ['active', 'succeeded']
      }
    },
    attachTypeLabel() {
      switch (this.attachType) {
        case 'card': return this.$t('payment-method-ui.add-card')
        case 'sepa_debit': return this.$t('payment-method-ui.add-sepa')
        case 'bancontact': return this.$t('payment-method-ui.add-bancontact')
        case 'apple_pay': return this.$t('payment-method-ui.add-apple-pay')
        case 'google_pay': return this.$t('payment-method-ui.add-google-pay')
        default: return ''
      }
    },
    headers() {
      const headers = [
        { text: this.$t('type'), value: 'type', sortable: false },
        { text: this.$t('status'), value: 'status', sortable: false },
        { text: this.$t('created'), value: 'createdAt', sortable: false },
        { text: this.$t('payment-method-ui.billing-name'), value: 'billingName', sortable: false },
        { text: this.$t('summary'), value: 'summary', sortable: false }
      ]

      if (!this.hideActions) {
        headers.push({ text: this.$t('actions'), value: 'actions', sortable: false, align: 'center' })
      }

      return headers
    },
  },

  watch: {
    organizationId() {
      if (this.autoLoad) {
        this.tableOptions.page = 1
        this.pagination.offset = 0
        this.reload()
      }
    },
    customerId() {
      if (this.autoLoad) {
        this.tableOptions.page = 1
        this.pagination.offset = 0
        this.reload()
      }
    },
    attachDialog(val) {
      if (!val) {
        this.destroyCardElement(); this.destroyIbanElement(); this.destroyPRButton(); this.errorBanner = null; this.submitting = false; this.redirecting = false
      } else { this.$nextTick(() => this.initStripe()) }
    }
  },

  mounted() {
    if (this.autoLoad) {
      // Handle Stripe return (for redirects)
      this.handleStripeReturn()
    }
  },

  methods: {
    applyStatusFilters() {
      // Reload data when filters change
      this.reloadFromStart()
    },

    async handleStripeReturn() {
      const { redirect_status, setup_intent } = this.$route.query

      // No Stripe params, nothing to do
      if (!redirect_status && !setup_intent) {
        return
      }

      // Clean URL immediately to avoid reload loops
      this.$router.replace({ query: {} })

      // Close attach dialog to avoid reopen issues
      this.attachDialog = false

      // Case 1: redirect_status provided by Stripe (Bancontact, etc.)
      if (redirect_status === 'succeeded') {
        this.notifySuccess('payment-method-ui.payment-method-added-success')
        await this.reloadFromStart({ forceSync: true })

        // Check if Bancontact is present in data
        const hasBancontact = this.items.some(item => {
          const itemType = item.type || item.pmType || item.details?.type
          return itemType === 'bancontact'
        })

        // If yes, wait 2 seconds for SEPA generated by webhook and reload
        if (hasBancontact) {
          await new Promise(resolve => setTimeout(resolve, 2000))
          await this.reloadFromStart({ forceSync: true })
        } else {
          console.log('⚠️ [Bancontact] No Bancontact detected, no second reload')
        }
        return
      }

      if (redirect_status === 'failed') {
        this.notifyError('payment-method-ui.payment-method-failed')
        return
      }

      // Case 2: setup_intent present but no redirect_status
      // Need to check status via backend
      if (setup_intent) {
        try {
          const setupIntentData = await PaymentMethodRepository.getSetupIntentStatus(setup_intent, this.apiParams)

          if (setupIntentData?.status === 'succeeded') {
            this.notifySuccess('payment-method-ui.payment-method-added-success')
            await this.reloadFromStart({ forceSync: true })

            // Check if Bancontact is present
            const hasBancontact = this.items.some(item => item.type === 'bancontact')

            // If yes, wait 2 seconds for SEPA generated by webhook and reload
            if (hasBancontact) {
              await new Promise(resolve => setTimeout(resolve, 2000))
              await this.reloadFromStart({ forceSync: true })
            }
          } else if (setupIntentData?.status === 'requires_payment_method' || setupIntentData?.status === 'canceled') {
            this.notifyError('payment-method-ui.payment-method-failed')
          } else {
            // Other statuses (processing, requires_action, etc.) : reload to see current state
            await this.reloadFromStart()
          }
        } catch (err) {
          // On error, reload anyway to show current state
          await this.reloadFromStart()
        }
      }
    },

    async reload({ forceSync = false } = {}) {
      this.loading = true
      this.error = null
      try {
        const params = {
          ...this.apiParams,
          limit: this.pagination.limit,
          offset: this.pagination.offset,
          type: this.filters.type
        }

        // Force sync with Stripe after adding a new payment method
        if (forceSync) {
          params.forceSync = true
        }

        // Add statuses[] parameter if any status is selected
        const statuses = this.selectedStatuses
        if (statuses.length > 0) {
          params.statuses = statuses
        }

        const response = await PaymentMethodRepository.listPaymentMethods(params)

        // Handle both old format (array) and new format (paginated object)
        if (Array.isArray(response)) {
          // Old format (no pagination) - can't have owner info as it's a raw array
          this.items = response.map(this.normalizeItem).sort((a,b) => (new Date(b.createdAt||0)) - (new Date(a.createdAt||0)))
          this.pagination.total = response.length
          this.pagination.hasMore = false
          this.ownerInfo = null
        } else if (response.items) {
          // New format (with pagination) - items field
          this.items = (response.items || []).map(this.normalizeItem)
          this.pagination.total = response.total || 0
          this.pagination.limit = response.limit || 10
          this.pagination.offset = response.offset || 0
          this.pagination.hasMore = response.hasMore || false
          // Store owner info enriched by backend
          this.ownerInfo = response.owner || null
        } else if (response.data) {
          // Alternative format with data field
          this.items = (response.data || []).map(this.normalizeItem)
          this.pagination.total = response.total || 0
          this.pagination.limit = response.limit || 10
          this.pagination.offset = response.offset || 0
          this.pagination.hasMore = response.hasMore || false
          // Store owner info enriched by backend
          this.ownerInfo = response.owner || null
        } else {
          this.items = []
          this.pagination.total = 0
          this.ownerInfo = null
        }

        this.$emit('loaded', this.items)
      } catch (err) {
        this.error = err.response?.data?.message || err.message
        this.$emit('error', err)
      } finally {
        this.loading = false
      }
    },

    async reloadFromStart({ forceSync = false } = {}) {
      // Reset to page 1 and reload
      this.tableOptions.page = 1
      this.pagination.offset = 0
      await this.reload({ forceSync })
    },

    async manualRefresh() {
      // Sync with Stripe and reload in one call
      try {
        this.syncLoading = true
        await this.reloadFromStart({ forceSync: true })
        this.$emit('synced')
      } catch (err) {
        this.notifyError('payment-method-ui.sync-failed')
      } finally {
        this.syncLoading = false
      }
    },

    onTableOptionsChange(options) {
      // v-data-table sends pagination/sort options
      // Update our pagination and reload
      const { page, itemsPerPage } = options

      this.pagination.limit = itemsPerPage
      this.pagination.offset = (page - 1) * itemsPerPage

      this.reload()
    },

    normalizeItem(it) {
      const createdAt = it.createdAt || it.created || it.created_at || null
      const type = it.pmType || it.type || it.details?.type || null
      const summary = it.pmSummary || it.summary || it.details?.summary || (it.card ? this.cardSummary(it.card) : null)
      const setupIntent = it.setupIntent || (it.setupIntentId ? { id: it.setupIntentId } : null)
      const billing = it.billing || it.details?.billing || { name: it.card?.name || null, email: null }
      const mandate = it.mandate || null
      const status = it.status || it.setupIntent?.status || null
      const stripePaymentMethodId = it.stripePaymentMethodId || it.paymentMethod || null
      return { ...it, createdAt, type, summary, setupIntent, billing, mandate, status, stripePaymentMethodId }
    },

    statusColor(status) {
      switch (status) { case 'succeeded': return 'green'; case 'processing': return 'blue'; case 'requires_action': return 'orange'; case 'requires_payment_method': return 'orange'; case 'canceled': return 'grey'; case 'failed': return 'red'; default: return 'grey' }
    },

    requiresAuthentication(item) {
      // Check if authentication is required based on PM status, setupIntent status, or validated flag
      if (item.validated === true) return false
      return item.status === 'requires_action' ||
             item.setupIntentStatus === 'requires_action' ||
             item.setupIntent?.status === 'requires_action'
    },

    typeLabel(t) {
      const key = { card: this.$t('payment-method.payment-method') && this.$t('payment-method.type.card') || 'card', sepa_debit: 'SEPA', deferred: this.$t('payment-method.type.deferred') || 'deferred', bancontact: 'Bancontact', apple_pay: 'Apple Pay', google_pay: 'Google Pay' }
      return key[t] || t || ''
    },

    formatDate(val) { if (!val) return ''; try { return new Date(val).toLocaleString() } catch (e) { return String(val) } },

    cardSummary(card) { if (!card) return ''; const brand = (card.brand || '').toString().toUpperCase(); const last4 = card.last4 || '••••'; const exp = card.expMonth && card.expYear ? `(${String(card.expMonth).padStart(2,'0')}/${String(card.expYear).slice(-2)})` : ''; return `${brand ? brand.charAt(0)+brand.slice(1).toLowerCase() : ''} •••• ${last4} ${exp}`.trim() },

    confirmDetach(pm) {
      // Use database ID (UUID)
      const idForDelete = pm.id
      if (!idForDelete) {
        this.notifyError('Payment method ID not found')
        return
      }
      this.$dialog?.confirm({text: this.$t('are-you-sure')}).then(async ok => {
        if (!ok) return
        try {
          this.showLoading(true)
          const res = await PaymentMethodRepository.deletePaymentMethod(idForDelete, this.apiParams)
          if (res && res.deleted === false) { this.notifyError('delete-failed') }
          else { this.notifySuccess('deleted') }
          this.reload()
          this.showLoading(false)
          this.$emit('deleted', pm)
        } catch (err) {
          this.notifyError(err)
          this.showLoading(false)
          this.$emit('error', err)
        }
      })
    },

    openAttach(type) {
      this.attachType = type
      this.attachForm.paymentMethodId = ''
      this.attachResult = null

      // Pre-fill name and email with owner info (enriched by backend)
      const defaultName = this.ownerInfo?.details?.invoiceContactName || this.ownerInfo?.name || ''
      const defaultEmail = this.ownerInfo?.details?.invoiceContactEmail || this.ownerInfo?.email || ''

      this.cardName = defaultName
      this.ibanName = defaultName
      this.ibanEmail = defaultEmail
      this.contactName = defaultName
      this.contactEmail = defaultEmail

      this.attachDialog = true
    },

    async retryAuthentication(pm) {
      // Get clientSecret from payment method data
      const clientSecret = pm.setupIntent?.clientSecret || pm.setupIntentClientSecret
      const paymentMethodId = pm.stripePaymentMethodId || pm.paymentMethod

      if (!clientSecret) {
        this.notifyError('payment-method-ui.no-setup-intent')
        return
      }

      if (!paymentMethodId) {
        this.notifyError('payment-method-ui.no-payment-method')
        return
      }

      try {
        this.showLoading(true)

        // Initialize Stripe if not already done
        if (!this.stripe) {
          this.stripe = await StripeService.initStripe()
        }

        // Use Stripe.js to handle 3DS authentication directly
        // Pass payment_method explicitly as it may have been detached after failed 3DS
        const { setupIntent, error } = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: paymentMethodId
        })

        if (error) {
          // Check if PaymentMethod was consumed/detached by Stripe (can't be reused)
          if (error.code === 'payment_method_unattached' ||
              error.message?.includes('may not be used again') ||
              error.message?.includes('was detached')) {
            // PaymentMethod is invalid - delete it and ask user to create a new one
            try {
              await PaymentMethodRepository.deletePaymentMethod(pm.id, this.apiParams)
              this.notifyError('payment-method-ui.pm-expired-deleted')
            } catch (deleteErr) {
              this.notifyError('payment-method-ui.pm-expired')
            }
            await this.reloadFromStart()
          } else if (error.code === 'setup_intent_authentication_failure') {
            this.notifyError('payment-method-ui.authentication-failed')
          } else {
            this.notifyError(error.message)
          }
        } else if (setupIntent.status === 'succeeded') {
          // Authentication successful!
          this.notifySuccess('payment-method-ui.authentication-success')
          await this.reloadFromStart({ forceSync: true })
        } else if (setupIntent.status === 'requires_payment_method') {
          // Card was declined or needs new payment method
          this.notifyError('payment-method-ui.setup-failed-retry')
        } else {
          // Other status (processing, etc.)
          this.notifyInfo('payment-method-ui.processing')
          await this.reloadFromStart()
        }
      } catch (err) {
        this.notifyError(err?.message || err)
      } finally {
        this.showLoading(false)
      }
    },

    async attachByPmId() {
      if (!this.attachForm.paymentMethodId) { this.notifyError(this.$t('payment-method-ui.pm-id') + ' ?'); return }
      this.actionLoading = true
      try {
        // Build returnUrl (without params - Stripe will add setup_intent and redirect_status)
        const baseUrl = this.organizationId
          ? `/organizations/${this.organizationId}/payment-methods`
          : `/customer/${this.customerId}/payment-methods`
        const returnUrl = window.location.origin + baseUrl

        const res = await PaymentMethodRepository.addPaymentMethod({
          ...this.apiParams,
          paymentMethodId: this.attachForm.paymentMethodId,
          returnUrl
        })
        this.attachResult = res

        if (res?.status === 'succeeded') {
          // Immediate success
          this.attachDialog = false
          this.notifySuccess('add-success')
          this.reload()
          this.$emit('added', res)
        } else if (res?.requiresAction && res?.nextAction) {
          // 3DS authentication required - Automatic redirection
          this.attachDialog = false
          this.notifyInfo('payment-method-ui.redirecting-auth')
          window.location.href = res.nextAction
        } else if (res?.status === 'requires_action' && res?.nextAction) {
          // Alternative if requiresAction not provided
          this.attachDialog = false
          this.notifyInfo('payment-method-ui.redirecting-auth')
          window.location.href = res.nextAction
        }
      } catch (err) {
        this.notifyError(err)
        this.$emit('error', err)
      } finally { this.actionLoading = false }
    },

    openDetail(pm) {
      this.selectedPaymentMethod = pm
      this.detailDialog = true
    },

    openTransactions(pm) {
      this.selectedPaymentMethod = pm
      this.transactionsDialog = true
    },

    openEditBilling(pm) { this.editTarget = pm; this.editForm.name = pm?.details?.billing?.name || pm?.card?.name || ''; this.editDialog = true },

    async saveBillingName() {
      if (!this.editTarget) return
      const sid = this.editTarget.setupIntent?.id || this.editTarget.setupIntentId || this.editTarget.id
      if (!sid) return
      this.actionLoading = true
      try {
        await PaymentMethodRepository.updatePaymentMethod(sid, {
          ...this.apiParams,
          name: this.editForm.name || ''
        })
        this.notifySuccess('update-success')
        this.editDialog = false
        this.reload()
        this.$emit('updated', this.editTarget)
      } catch (err) {
        this.notifyError(err)
        this.$emit('error', err)
      } finally { this.actionLoading = false }
    },

    openCharge(pm) { this.chargeTarget = pm; this.chargeForm.amount = null; this.chargeForm.currency = 'EUR'; this.chargeForm.capture = true; this.chargeForm.metadataRaw = ''; this.chargeResult = null; this.chargeDialog = true },

    async submitCharge() {
      if (!this.chargeTarget) return
      if (!this.chargeForm.amount || !this.chargeForm.currency) { this.notifyError('required'); return }
      let metadata
      if (this.chargeForm.metadataRaw) { try { metadata = JSON.parse(this.chargeForm.metadataRaw) } catch (e) { metadata = undefined } }
      const paymentMethodId = this.chargeTarget.id || this.chargeTarget.paymentMethod || this.chargeTarget.stripePaymentMethodId
      // Convert amount to cents for Stripe
      const amountInCents = Math.round(Number(this.chargeForm.amount) * 100)
      this.actionLoading = true
      try {
        const result = await PaymentMethodRepository.createPayment({
          ...this.apiParams,
          paymentMethodId,
          amount: amountInCents,
          currency: this.chargeForm.currency,
          metadata,
          capture: !!this.chargeForm.capture
        })

        // Check if authentication is required
        if (result.status === 'requires_action' && result.client_secret) {
          // Normalize to snake_case for PaymentAuthDialog
          this.authPaymentResponse = {
            ...result,
            client_secret: result.clientSecret || result.client_secret,
            next_action: result.nextAction || result.next_action
          }
          this.chargeDialog = false
          this.authDialog = true
        } else {
          // Payment succeeded directly or processing
          this.chargeResult = JSON.stringify(result, null, 2)
          this.notifySuccess('add-success')
          this.chargeDialog = false
          this.reload()
          this.$emit('charged', result)
        }
      } catch (err) {
        this.notifyError(err)
        this.$emit('error', err)
      } finally { this.actionLoading = false }
    },

    handleAuthSuccess(paymentIntent) {
      this.notifySuccess('add-success')
      this.authDialog = false
      this.reload()
      this.$emit('auth-success', paymentIntent)
    },

    handleAuthError(error) {
      this.notifyError(error?.message || error)
      this.$emit('auth-error', error)
      // Don't close dialog to allow retry
    },

    // Stripe helpers
    async initStripe() {
      try {
        // Initialize Stripe via StripeService (with automatic cache)
        this.stripe = await StripeService.initStripe()

        // Create elements
        if (!this.elements && this.stripe) {
          this.elements = this.stripe.elements()
        }

        // Mount appropriate elements
        this.$nextTick(() => {
          if (this.attachType === 'card') this.mountCardElement()
          if (this.attachType === 'sepa_debit') this.mountIbanElement()
          if (this.attachType === 'apple_pay') this.mountPaymentRequestButton('apple')
          if (this.attachType === 'google_pay') this.mountPaymentRequestButton('google')
        })
      } catch (err) {
        this.cardError = 'Failed to load Stripe configuration'
      }
    },

    mountCardElement() {
      try {
        this.destroyCardElement()
        if (!this.elements) return

        const style = {
          base: { fontSize: '16px', color: '#32325d', '::placeholder': { color: '#aab7c4' } },
          invalid: { color: '#fa755a' }
        }

        // Create Card Element without postal code
        this.cardElement = this.elements.create('card', {
          style,
          hidePostalCode: true
        })

        const container = document.getElementById('zms-card-element')
        if (container) {
          this.cardElement.mount(container)
          this.cardElement.on('change', ev => {
            this.cardError = ev.error ? ev.error.message : null
          })
        }
      } catch (e) {
        this.cardError = e?.message || String(e)
      }
    },

    destroyCardElement() { try { if (this.cardElement) { this.cardElement.unmount(); this.cardElement.destroy() } } finally { this.cardElement = null } },

    mountIbanElement() {
      try { this.destroyIbanElement(); if (!this.elements) return; const style = { base: { fontSize: '16px', color: '#32325d', '::placeholder': { color: '#aab7c4' } }, invalid: { color: '#fa755a' } }; this.ibanElement = this.elements.create('iban', { style, supportedCountries: ['SEPA'] }); const container = document.getElementById('zms-iban-element'); if (container) { this.ibanElement.mount(container); this.ibanElement.on('change', ev => { this.ibanError = ev.error ? ev.error.message : null }) } }
      catch (e) { this.ibanError = e?.message || String(e) }
    },

    destroyIbanElement() { try { if (this.ibanElement) { this.ibanElement.unmount(); this.ibanElement.destroy() } } finally { this.ibanElement = null } },

    // Payment Request Button (Apple Pay / Google Pay)
    async mountPaymentRequestButton(kind) {
      try {
        if (!this.stripe) return
        this.destroyPRButton()
        const country = this.organizationData?.billingDetails?.country?.toUpperCase() || 'FR'
        const currency = this.currentCurrency.toLowerCase()
        const label = this.$t(kind === 'apple' ? 'payment-method-ui.apple-pay-title' : 'payment-method-ui.google-pay-title')
        this.paymentRequest = this.stripe.paymentRequest({ country, currency, total: { label, amount: 100 }, requestPayerName: true, requestPayerEmail: true })
        const result = await this.paymentRequest.canMakePayment()
        if (kind === 'apple') this.appleAvailable = !!(result && result.applePay)
        if (kind === 'google') this.googleAvailable = !!(result && (result.googlePay || result))
        const mountId = kind === 'apple' ? 'zms-apple-pay-button' : 'zms-google-pay-button'
        if ((kind === 'apple' && this.appleAvailable) || (kind === 'google' && this.googleAvailable)) {
          this.prButtonElement = this.elements.create('paymentRequestButton', { paymentRequest: this.paymentRequest })
          const container = document.getElementById(mountId)
          if (container) this.prButtonElement.mount(container)
          this.paymentRequest.on('paymentmethod', async ev => {
            this.emitPmEvent && this.emitPmEvent('submit', kind)
            try {
              const pmId = ev.paymentMethod?.id
              if (!pmId) throw new Error('No payment method from wallet')
              if (this.saveForLater) {
                const baseUrl = this.organizationId
                  ? `/organizations/${this.organizationId}/payment-methods`
                  : `/customer/${this.customerId}/payment-methods`
                const returnUrl = window.location.origin + baseUrl

                const res = await PaymentMethodRepository.addPaymentMethod({
                  ...this.apiParams,
                  paymentMethodId: pmId,
                  returnUrl
                })

                // Handle 3DS redirect if needed
                if (res?.requiresAction && res?.nextAction) {
                  ev.complete('fail')
                  window.location.href = res.nextAction
                } else if (res?.status === 'requires_action' && res?.nextAction) {
                  ev.complete('fail')
                  window.location.href = res.nextAction
                } else {
                  ev.complete('success')
                  this.notifySuccess('add-success')
                  this.attachDialog = false
                  this.reload()
                  this.$emit('added', res)
                }
              } else {
                ev.complete('fail')
                this.errorBanner = this.$t('payment-method-ui.wallet-one-shot-not-implemented')
              }
            } catch (e) { ev.complete('fail'); this.errorBanner = e?.response?.data?.message || e?.message || String(e) }
          })
        }
      } catch (e) { this.errorBanner = e?.message || String(e) }
    },

    destroyPRButton() { try { if (this.prButtonElement) { this.prButtonElement.unmount(); this.prButtonElement.destroy() } } finally { this.paymentRequest = null; this.prButtonElement = null; this.appleAvailable = false; this.googleAvailable = false } },

    async submitSepa() {
      if (!this.stripe || !this.ibanElement) {
        this.ibanError = 'Stripe not properly initialized'
        return
      }
      if (!this.sepaAccepted) return

      this.pmCreating = true
      this.ibanError = null

      try {
        // 1. Créer SetupIntent côté backend (lié au Customer, sans PM)
        const res = await PaymentMethodRepository.createSetupIntent({
          ...this.apiParams,
          paymentMethodType: 'sepa_debit'
        })

        const clientSecret = res?.clientSecret || res?.client_secret
        if (!clientSecret) {
          throw new Error('Backend did not return clientSecret')
        }

        // 2. Créer le PM pour avoir l'ID (nécessaire pour prepare-view du mandat)
        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
          type: 'sepa_debit',
          sepa_debit: this.ibanElement,
          billing_details: {
            name: this.ibanName || undefined,
            email: this.ibanEmail || undefined
          }
        })

        if (error) {
          this.ibanError = error.message
          return
        }

        if (paymentMethod?.id) {
          // Stocker le clientSecret pour la confirmation après signature du mandat
          this.sepaClientSecret = clientSecret

          // 3. Ouvrir le dialogue de mandat personnalisé
          await this.openSepaMandateDialog(paymentMethod.id)
        }
      } catch (e) {
        this.ibanError = e?.response?.data?.message || e?.message || String(e)
      } finally {
        this.pmCreating = false
      }
    },

    // SEPA Mandate Dialog methods
    async openSepaMandateDialog(paymentMethodId) {
      this.sepaMandateDialog = true
      this.mandateLoading = true
      this.mandateError = null
      this.mandateHtml = null
      this.mandateContentHash = null
      this.mandateSnapshotData = null
      this.mandatePaymentMethodId = paymentMethodId
      this.mandateConsent = false
      this.mandateAccepted = false
      this.mandatePdfUrl = null

      try {
        const locale = this.$i18n?.locale || 'fr'

        if (!this.organizationId && !this.customerId) {
          throw new Error('Organization ID or Customer ID is not defined')
        }

        const params = {
          ...this.apiParams,
          locale
        }

        // Add signatory info for organizations (required) and customers (optional)
        if (this.isOrganizationContext || this.isCustomerContext) {
          if (this.signatoryFirstName) params.signatoryFirstName = this.signatoryFirstName
          if (this.signatoryLastName) params.signatoryLastName = this.signatoryLastName
        }

        const res = await PaymentMethodRepository.prepareMandateView(paymentMethodId, params)

        this.mandateHtml = res.renderedHtml || res.html || res.mandateHtml
        this.mandateContentHash = res.contentHash
        this.mandateSnapshotData = res.snapshotData

        if (!this.mandateHtml || !this.mandateSnapshotData) {
          throw new Error('Missing mandate HTML or snapshot data')
        }
      } catch (e) {
        this.mandateError = e?.response?.data?.message || e?.message || String(e)
      } finally {
        this.mandateLoading = false
      }
    },

    closeSepaMandateDialog() {
      this.sepaMandateDialog = false
      this.mandateLoading = false
      this.mandateError = null
      this.mandateHtml = null
      this.mandateContentHash = null
      this.mandateSnapshotData = null
      this.mandatePaymentMethodId = null
      this.mandateMandateId = null
      this.mandateConsent = false
      this.mandateAccepting = false
      this.mandateAccepted = false
      this.mandatePdfUrl = null
    },

    async acceptSepaMandateSignature() {
      if (!this.mandateConsent || !this.mandateContentHash || !this.mandateSnapshotData || !this.mandatePaymentMethodId) {
        return
      }

      this.mandateAccepting = true
      this.mandateError = null

      try {
        this.showLoading(true)

        // 1. Confirmer le SetupIntent avec le PM pour l'attacher au Customer
        if (this.sepaClientSecret) {
          const { setupIntent, error } = await this.stripe.confirmSepaDebitSetup(this.sepaClientSecret, {
            payment_method: this.mandatePaymentMethodId
          })

          if (error) {
            this.mandateError = error.message
            return
          }

          if (setupIntent.status !== 'succeeded' && setupIntent.status !== 'processing') {
            this.mandateError = `SetupIntent status: ${setupIntent.status}`
            return
          }
        }

        // 2. Appeler l'endpoint acceptMandate pour enregistrer le mandat personnalisé
        const clientIp = '0.0.0.0' // Backend should ideally detect this
        const userAgent = navigator.userAgent || ''

        const res = await PaymentMethodRepository.acceptMandate({
          contentHash: this.mandateContentHash,
          snapshotData: this.mandateSnapshotData,
          paymentMethodId: this.mandatePaymentMethodId,
          clientIp,
          userAgent,
          ...this.apiParams
        })

        // Store mandate ID if returned
        this.mandateMandateId = res.mandateId || res.mandate?.id

        // Get PDF URL if mandate was created
        if (this.mandateMandateId) {
          try {
            const pdfRes = await PaymentMethodRepository.getMandatePdfUrl(this.mandateMandateId)
            this.mandatePdfUrl = pdfRes.url || pdfRes.pdfUrl
          } catch (e) {
            // Not critical if PDF URL fails
            console.warn('Failed to get mandate PDF URL')
          }
        }

        // Mark as accepted
        this.mandateAccepted = true

        // Show success notification
        this.notifySuccess('payment-method-ui.mandate-accepted')

        // After a delay, close dialog and reload with forceSync
        setTimeout(async () => {
          this.closeSepaMandateDialog()
          this.attachDialog = false
          this.sepaClientSecret = null // Reset
          await this.reloadFromStart({ forceSync: true })
        }, 2000)

      } catch (e) {
        this.mandateError = e?.response?.data?.message || e?.message || String(e)
      } finally {
        this.mandateAccepting = false
        this.showLoading(false)
      }
    },

    async submitBancontact() {
      this.errorBanner = null
      if (this.currentCurrency !== 'EUR') return
      if (!this.contactName || !this.contactEmail) return
      if (!this.stripe) { this.initStripe(); if (!this.stripe) return }
      this.submitting = true; this.redirecting = false; this.redirectUrl = null
      try {
        const baseUrl = this.organizationId
          ? `/organizations/${this.organizationId}/payment-methods`
          : `/customer/${this.customerId}/payment-methods`
        const returnUrl = window.location.origin + baseUrl

        // Create SetupIntent for Bancontact (micro-payment 0.02€ auto-refunded)
        const res = await PaymentMethodRepository.createSetupIntent({
          ...this.apiParams,
          paymentMethodType: 'bancontact',
          returnUrl
        })

        const clientSecret = res?.clientSecret || res?.client_secret
        if (!clientSecret) { throw new Error(this.$t('payment-method-ui.backend-required')) }

        this.redirecting = true

        // Confirm SetupIntent with Bancontact
        const confirmRes = await this.stripe.confirmBancontactSetup(clientSecret, {
          payment_method: {
            billing_details: {
              name: this.contactName,
              email: this.contactEmail
            }
          },
          return_url: returnUrl
        })

        if (confirmRes?.error) {
          this.redirecting = false
          this.errorBanner = confirmRes.error.message
        } else if (confirmRes?.setupIntent?.next_action?.redirect_to_url?.url) {
          this.redirectUrl = confirmRes.setupIntent.next_action.redirect_to_url.url
        }
      } catch (e) {
        this.errorBanner = e?.response?.data?.message || e?.message || String(e)
      } finally {
        this.submitting = false
      }
    },

    async submitCard() {
      if (!this.stripe || !this.cardElement) {
        await this.initStripe()
        if (!this.stripe || !this.cardElement) return
      }

      this.pmCreating = true
      this.cardError = null

      try {
        // 1. Créer SetupIntent côté backend (lié au Customer, sans PM)
        const baseUrl = this.organizationId
          ? `/organizations/${this.organizationId}/payment-methods`
          : `/customer/${this.customerId}/payment-methods`
        const returnUrl = window.location.origin + baseUrl

        const res = await PaymentMethodRepository.createSetupIntent({
          ...this.apiParams,
          paymentMethodType: 'card',
          returnUrl
        })

        const clientSecret = res?.clientSecret || res?.client_secret
        if (!clientSecret) {
          throw new Error('Backend did not return clientSecret')
        }

        // 2. Confirmer avec les données de carte - PM créé ET attaché en une fois
        const { setupIntent, error } = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: this.cardElement,
            billing_details: { name: this.cardName || undefined }
          }
        })

        if (error) {
          this.cardError = error.message
          return
        }

        // 3. Gérer le résultat
        if (setupIntent.status === 'succeeded') {
          this.attachDialog = false
          this.notifySuccess('payment-method-ui.payment-method-added-success')
          await this.reloadFromStart({ forceSync: true })
        } else if (setupIntent.status === 'requires_action') {
          // 3DS en cours - normalement géré automatiquement par confirmCardSetup
          // Si on arrive ici, c'est que la redirection n'a pas eu lieu
          this.notifyInfo('payment-method-ui.redirecting-auth')
        }
      } catch (e) {
        this.cardError = e?.response?.data?.message || e?.message || String(e)
      } finally {
        this.pmCreating = false
      }
    },

    async submitAttach() {
      try {
        if (this.attachType === 'card') { await this.submitCard(); return }
        if (this.attachType === 'sepa_debit') { await this.submitSepa(); return }
        if (this.attachType === 'bancontact') { await this.submitBancontact(); return }
        if (this.attachType === 'apple_pay' || this.attachType === 'google_pay') { if (this.paymentRequest) { this.paymentRequest.show() } else { this.errorBanner = this.$t('payment-method-ui.backend-required') } return }
      } catch (e) { this.notifyError(e) }
    },

    isBottomDisabled() {
      if (this.attachType === 'sepa_debit') {
        const baseValidation = !this.sepaAccepted || !this.ibanName || !this.ibanEmail || this.pmCreating
        const signatoryValidation = this.isOrganizationContext && (!this.signatoryFirstName || !this.signatoryLastName)
        return baseValidation || signatoryValidation
      }
      if (this.attachType === 'bancontact') return !this.contactName || !this.contactEmail || this.currentCurrency !== 'EUR' || this.submitting
      if (this.attachType === 'apple_pay') return !this.appleAvailable
      if (this.attachType === 'google_pay') return !this.googleAvailable
      return false
    },
  },

  beforeDestroy() { this.destroyCardElement(); this.destroyIbanElement() }
}
</script>

<style lang="scss">
.payment-method-list {}
</style>
